import React, { useState } from 'react';
import { HistoricalSummary } from '../../../shared/types';
import TeamBreakdown from './TeamBreakdown';
import './HistoricalView.css';
import { getOwnerColor } from '../utils/ownerColors';
import { getFantasyTeamName } from '../utils/teamNames';

interface HistoricalViewProps {
  historicalData: HistoricalSummary[];
  onSeasonSelect: (season: number) => void;
}

const HistoricalView: React.FC<HistoricalViewProps> = ({ historicalData, onSeasonSelect }) => {
  const [expandedSeason, setExpandedSeason] = useState<number | null>(null);

  const toggleSeason = (year: number) => {
    setExpandedSeason(expandedSeason === year ? null : year);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (historicalData.length === 0) {
    return (
      <div className="historical-view">
        <div className="no-history">
          <h3>No Historical Data</h3>
          <p>Complete your first season to see historical standings here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="historical-view">
      <h2>Historical Standings</h2>
      
      <div className="historical-seasons">
        {historicalData.map((season) => (
          <div key={season.year} className="historical-season-card">
            <div 
              className="season-header"
              onClick={() => toggleSeason(season.year)}
            >
              <div className="season-info">
                <h3>{season.year} Season</h3>
                <div className="champion-info">
                  <span className="champion-badge">🏆 Champion</span>
                  <span className="champion-name" style={{ color: getOwnerColor(season.champion) }}>
                    {getFantasyTeamName(season.champion)}
                  </span>
                  <span className="champion-score">({season.championScore} points)</span>
                </div>
              </div>
              
              <div className="season-actions">
                <button
                  className="view-details-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSeasonSelect(season.year);
                  }}
                >
                  View Full Season
                </button>
                <span className={`expand-icon ${expandedSeason === season.year ? 'rotated' : ''}`}>
                  ▼
                </span>
              </div>
            </div>

            {expandedSeason === season.year && (
              <div className="season-details">
                <div className="final-standings">
                  <h4>Final Standings</h4>
                  <div className="standings-table-mini">
                    {season.standings.map((standing) => (
                      <div key={standing.name} className={`standing-row rank-${standing.rank}`}>
                        <span className="rank">#{standing.rank}</span>
                        <span className="name" style={{ color: getOwnerColor(standing.name) }}>
                          {getFantasyTeamName(standing.name)}
                        </span>
                        <span className="score">{standing.totalScore} pts</span>
                        <span className="record">
                          {standing.regularSeasonWins}W-{standing.playoffWins}P
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="season-meta">
                    <p className="finalized-date">
                      Season completed: {formatDate(season.finalizedDate)}
                    </p>
                  </div>
                </div>

                <div className="team-records">
                  <h4>Team Records</h4>
                  {season.standings.map((standing) => (
                    <div key={`${standing.name}-teams`} className="member-team-records">
                      <TeamBreakdown 
                        playerName={standing.name}
                        teams={standing.teams}
                        teamRecords={standing.teamRecords}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoricalView;
